import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'

import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { getStorage } from '@src/utils/enhancedStorage'

import FormHeader from '../../../../../components/Auth/FormHeader'
import Button from '../../../../../components/Button'
import { BoxStyledErrorMessage } from '../../../../../components/FormElements'
import { StyledVerificationMessage } from '../../../../../components/FormElements/StyledVerificationMessage'
import { GoogleSignIn } from '../../../../Auth/GoogleSignIn/GoogleSignIn'
import { useIsB2bAuthSource } from '../../../../Auth/hooks/use-is-b2b-auth-source'
import { ContinueButton } from '../../../../LogIn/Components/ContinueButton'
import { Email } from '../../../../LogIn/Components/Email'
import styles from '../../../../LogIn/Components/loginComponents.module.scss'
import { TnC } from '../../../../LogIn/Components/TnC'
import useForm from '../../../../LogIn/hook/useForm'

import newStyles from './initialPage.module.scss'

interface InitialPagePropTypes {
  serverError?: { message: string }
  unverifiedAccount?: boolean
  resendVerificationEmail: (email?: string) => void
  verificationEmailSent?: boolean
  email?: string
  loading?: boolean
  onPageStateChange: (email: string) => void
  error?: string | null
  /** SSR-computed B2B auth source (query param or OAuth-callback cookie) — see useIsB2bAuthSource. */
  isB2BAuthSource?: boolean
}

// @i18n-ignore
const RESEND_BUTTON_TRACK_CATEGORY = 'login'
// @i18n-ignore
const LOGIN_FORM_A_INITIAL = 'login-form-A-Initial'

const InitialPage = ({
  serverError,
  unverifiedAccount,
  resendVerificationEmail,
  verificationEmailSent,
  email,
  loading,
  onPageStateChange,
  error,
  isB2BAuthSource,
}: InitialPagePropTypes) => {
  const { t } = useTranslation('common')
  const isB2bSource = useIsB2bAuthSource(isB2BAuthSource)
  const [errors, setErrors] = useState({})
  const { email: userEmail, onEmailChange } = useForm({ email })
  const searchParams = useSearchParams()
  const [fromAction, setFromAction] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    setFromAction(getStorage().getItem('fromContactAction', { isGlobal: true }) ?? searchParams?.get('from') ?? '')
  }, [searchParams])
  // 3.3.1/3.3.3: a failed submit must move focus to the field that needs correcting, not just render
  // a message. Keyed off `aria-invalid` so it finds the first bad field in DOM order without every
  // field component having to forward a ref. Safe to run on any `errors` change because this form
  // only validates on submit — a form validating on change would steal focus mid-typing.
  useEffect(() => {
    if (Object.keys(errors).length === 0) return
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
  }, [errors])
  const onInitialPageSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    let error = ''
    if (!userEmail) {
      error = t('please_fill_in_the_email')
    } else if (!validateEmail(userEmail)) {
      error = t('please_enter_valid_email_address')
    }
    if (error) {
      setErrors({
        username: {
          message: error,
        },
      })
    } else {
      onPageStateChange(userEmail)
    }
  }

  return (
    <form ref={formRef} onSubmit={onInitialPageSubmit} id="login-form-A-Initial" noValidate>
      <FormHeader>
        <h1 className={styles.formHeading}>{t('sign_in_register')}</h1>
        {!fromAction && (
          <div className={styles.formSubHeading}>
            {t(isB2bSource ? 'b2b_sign_in_register_message' : 'free_shipping_365_day_ret_atbvw9')}
          </div>
        )}
        {fromAction && <div className={styles.formSubHeading}>{t('order_management_signin')}</div>}
      </FormHeader>
      {serverError && (
        <BoxStyledErrorMessage dangerouslySetInnerHTML={{ __html: getSanitizedHtml(serverError.message) }} />
      )}
      {error && <BoxStyledErrorMessage>{error}</BoxStyledErrorMessage>}
      {unverifiedAccount && (
        <StyledVerificationMessage>
          {t('cannot_find_verification_mail')}
          <Button
            onClick={() => {
              resendVerificationEmail(email)
            }}
            trackParams={{
              trackInternal: true,
              category: RESEND_BUTTON_TRACK_CATEGORY,
              action: 'account_login_unverified',
              context: {
                login_email: email ?? '',
                lb_user_id: getlbUserId(),
              },
            }}
            className={styles.unVerifiedMessage}
          >
            {t('resend_one')}
          </Button>
        </StyledVerificationMessage>
      )}
      {verificationEmailSent && <StyledVerificationMessage>{t('verification_e_mail_sent')}</StyledVerificationMessage>}
      <GoogleSignIn />
      <div className={styles.divider}>{t('or')}</div>
      <Email errors={errors} defaultValue={email} value={userEmail} onChange={onEmailChange} />
      <ContinueButton loading={loading} form={LOGIN_FORM_A_INITIAL} />
      <div className={newStyles.footer}>
        <TnC />
      </div>
    </form>
  )
}

export { InitialPage }
